import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from 'ag-grid-community';
import { ReportWrapper } from './styles';
import { IInvoiceDetailReport, IInvoiceHeaderDescription, IInvoiceLineItemReport } from '@nkr/models/index';
import { IInvoiceDetailTotal } from './total';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { first, merge } from 'lodash';
import { Label, OverlayTrigger, Popover } from 'react-bootstrap';
import PopoverTemplate from '@nkr/components/popover-template';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: IInvoiceDetailReport[],
    invoiceLineItems: IInvoiceLineItemReport[],
    headerDescription: IInvoiceHeaderDescription,
    totals: IInvoiceDetailTotal,
    onGridReady: (e: GridReadyEvent) => void
}

const NON_FEE_COLUMNS = ['rank', 'InvoiceNumber', 'invoiceNumber', 'organType', 'center', 'centerAlias', 'centerState', 'amountDue', 'balanceCheck'];

const getBalanceDifference = (params: ValueGetterParams | ICellRendererParams) => {
    const columnTotal = params.columnApi.getAllGridColumns()
        .filter(column => NON_FEE_COLUMNS.indexOf(column.getColId()) < 0 && NON_FEE_COLUMNS.indexOf(column.getColDef().field) < 0)
        .reduce((total, column) => {
            let value = parseFloat(params.api.getValue(column, params.node));
            return total + (isNaN(value) ? 0 : value);
        }, 0);

    return columnTotal - (parseFloat(params.data.amountDue) || 0);
}

const Grid = (props: IProps) => {

    const getPopover = (invoiceNumber: number, columnDescription: string, total: string, lineItemsList: IInvoiceLineItemReport[]) => {

        if (props.invoiceLineItems) {
            let adjustments = lineItemsList.filter(lineItem => lineItem.invoiceNumber == invoiceNumber && lineItem.applyToDescription == columnDescription);
            if (adjustments && adjustments.length > 0) {
                let creditAdjustments = adjustments.filter(lineItem => lineItem.isCredit == true);
                let debitAdjustments = adjustments.filter(lineItem => lineItem.isCredit == false);
                return (
                    <div><b>{columnDescription}</b>
                        {creditAdjustments.map((item, index) => (
                            <div>Fee ${item.fee.toLocaleString()} + Line Item Credit ${item.amount.toLocaleString()}</div>
                        ))}
                        {debitAdjustments.map((item, index) => (
                            <div>Fee ${item.fee.toLocaleString()} + Line Item Fee ${item.amount.toLocaleString()}</div>
                        ))}
                        <br />Total Fee ${total}</div>
                );
            }
            else
                return <div><b>No Adjuistment</b></div>;
        }
        else {
            return <div><b>Empty</b></div>;
        }
    };

    const hasAdjustments = (invoiceNumber: number, columnDescription: string, lineItemsList: IInvoiceLineItemReport[]) => {

        if (props.invoiceLineItems) {
            let adjustment = lineItemsList.find(lineItem => lineItem.invoiceNumber == invoiceNumber && lineItem.applyToDescription == columnDescription);
            if (adjustment)
                return true;
            else
                return false;
        }
        else {
            return false;
        }
    };

    const formatFee = (value: any) => {
        const fee = parseFloat(value);
        return isNaN(fee) ? 'N/A' : fee.toLocaleString();
    };

    const itemDescr = props.headerDescription;
    const lineItems = props.invoiceLineItems;

    const columns = [
        {
            headerName: '',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'rank',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                    headerName: 'R#',
                    width: 40,
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        if (params.node.rowPinned) return <></>;   // ← skip pinned row
                        return <>{params.node.rowIndex + 1}</>;
                    },
                    valueGetter: (props: ValueGetterParams) => props.node.rowPinned ? '' : props.node.rowIndex + 1,
                    pinned: 'left',
                },
                {
                    colId: 'InvoiceNumber',
                    field: 'invoiceNumber',
                    headerName: 'Invoice Number',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Invoice<br />Number</SortableHeaderComponent>,
                    width: 115,
                    pinned: 'left',
                    pinnedRowCellRendererFramework: (props: ICellRendererParams) => {
                        return <div className='text-right'>Totals &gt;&gt;</div>;
                    },
                    colSpan: (params: any) => params.data.isSummary ? 2 : 1
                },
                {
                    field: 'organType',
                    headerName: 'Organ Type',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params}>Organ<br />Type</SortableHeaderComponent>,
                    width: 75,
                    pinned: 'left',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        if (params.node.rowPinned) return <></>;   // ← skip pinned row
                        let data = params.data as IInvoiceDetailReport;
                        let organType = data.organType == null ? '--' : data.organType == 1 ? 'L' : 'K';
                        return <Label bsStyle={data.organType == null ? 'default' : data.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
                    }
                },
                {
                    colId: 'center',
                    field: 'centerAlias',
                    headerName: 'Center',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
                    width: 155,
                    pinned: 'left',
                    comparator: Utility.stringSortComparator
                },

                {
                    colId: 'balanceCheck',
                    headerName: 'Balanced',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Bal</SortableHeaderComponent>,
                    width: 55,
                    pinned: 'left',
                    valueGetter: (params: ValueGetterParams) => Math.abs(getBalanceDifference(params)) < 0.005 ? 1 : 0,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let isBalanced = params.value == 1;
                        let message = isBalanced
                            ? 'All columns balance'
                            : `Not balanced - columns are off by ${getBalanceDifference(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                        return (
                            <OverlayTrigger rootClose placement='right' trigger={['hover', 'focus']} overlay={<PopoverTemplate dismiss={false}>{message}</PopoverTemplate>}>
                                <span
                                    className={isBalanced ? 'glyphicon glyphicon-ok' : 'glyphicon glyphicon-remove'}
                                    style={{ color: isBalanced ? '#3c763d' : '#a94442', fontSize: '115%' }}
                                />
                            </OverlayTrigger>
                        );
                    }
                },

                {
                    colId: 'centerState',
                    field: 'centerState',
                    headerName: 'Class',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Class</SortableHeaderComponent>,
                    width: 50,
                    pinned: 'left',
                    hide: true
                },
            ]
        },

        {
            headerName: 'DASH',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'dashFee',
                    field: 'dashFee',
                    headerName: itemDescr.dashMonthlyFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.dashMonthlyFeeDescr}</SortableHeaderComponent>,
                    width: 120,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.dashMonthlyFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.dashMonthlyFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'dashFeeLiver',
                    field: 'dashFeeLiver',
                    headerClass: 'list-header',
                    headerName: itemDescr.dashMonthlyFeeLiverDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.dashMonthlyFeeLiverDescr}</SortableHeaderComponent>,
                    width: 120,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.dashMonthlyFeeLiverDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.dashMonthlyFeeLiverDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                }
            ]
        },

        {
            groupId: 'transplant',
            headerName: 'Transplant',
            headerClass: 'list-group-header',
            openByDefault: true,
            children: [
                {
                    colId: 'transplantFee',
                    field: 'transplantFee',
                    headerName: itemDescr.matchingFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.matchingFeeDescr}</SortableHeaderComponent>,
                    width: 110,
                    columnGroupShow: 'open',
                    valueGetter: (params: ValueGetterParams) => {
                        let data = params.data as IInvoiceDetailReport;

                        return params.node.rowPinned
                            ? (data.transplantFee || 0) + (data.transplantLiverFee || 0)
                            : (data.organType == 1 ? data.transplantLiverFee : data.transplantFee);
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;

                        // Pinned totals row: combine kidney + liver since it isn't organ-specific
                        let feeValue = params.node.rowPinned
                            ? (data.transplantFee || 0) + (data.transplantLiverFee || 0)
                            : (data.organType == 1 ? data.transplantLiverFee : data.transplantFee);

                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.matchingFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.matchingFeeDescr, feeValue.toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{feeValue.toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{feeValue.toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'logisticsFee',
                    field: 'logisticsFee',
                    headerName: itemDescr.logisticsSupportFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.logisticsSupportFeeDescr}</SortableHeaderComponent>,
                    width: 110,
                    columnGroupShow: 'open',
                    valueGetter: (params: ValueGetterParams) => {
                        let data = params.data as IInvoiceDetailReport;

                        return params.node.rowPinned
                            ? (data.logisticsFee || 0) + (data.logisticsFeeLiver || 0)
                            : (data.organType == 1 ? data.logisticsFeeLiver : data.logisticsFee);
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;

                        // Pinned totals row: combine kidney + liver since it isn't organ-specific
                        let feeValue = params.node.rowPinned
                            ? (data.logisticsFee || 0) + (data.logisticsFeeLiver || 0)
                            : (data.organType == 1 ? data.logisticsFeeLiver : data.logisticsFee);

                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.logisticsSupportFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.logisticsSupportFeeDescr, feeValue.toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{feeValue.toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{feeValue.toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'shippingFee',
                    field: 'shippingFee',
                    headerName: itemDescr.kidneyShippingFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.kidneyShippingFeeDescr}</SortableHeaderComponent>,
                    width: 85,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.kidneyShippingFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.kidneyShippingFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'opoFee',
                    field: 'opoFee',
                    headerName: itemDescr.opoPackagingAndPerfusionDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.opoPackagingAndPerfusionDescr}</SortableHeaderComponent>,
                    width: 85,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.opoPackagingAndPerfusionDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.opoPackagingAndPerfusionDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'crossMatchKitFee',
                    field: 'crossMatchKitFee',
                    headerName: itemDescr.kitFulfillmentXmFreshDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.kitFulfillmentXmFreshDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.kitFulfillmentXmFreshDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.kitFulfillmentXmFreshDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'homeBloodDrawKit',
                    field: 'homeBloodDrawKit',
                    headerName: itemDescr.kitFulfillmentHomeBloodDrawDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.kitFulfillmentHomeBloodDrawDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.kitFulfillmentHomeBloodDrawDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.kitFulfillmentHomeBloodDrawDescr, formatFee(params.value), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{formatFee(params.value)}</a></div>
                                </OverlayTrigger>
                                : <>{formatFee(params.value)}</>
                        );
                    }
                },
                {
                    colId: 'remoteRiskPremium',
                    field: 'remoteRiskPremium',
                    headerName: itemDescr.remoteDirectRiskPremiumDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.remoteDirectRiskPremiumDescr}</SortableHeaderComponent>,
                    width: 85,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.remoteDirectRiskPremiumDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.remoteDirectRiskPremiumDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'incompatiblePairRiskPremium',
                    field: 'incompatiblePairRiskPremium',
                    headerName: itemDescr.incompatiblePairRiskPremiumDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.incompatiblePairRiskPremiumDescr}</SortableHeaderComponent>,
                    width: 105,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.incompatiblePairRiskPremiumDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.incompatiblePairRiskPremiumDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'privateInsuranceRiskPremium',
                    field: 'privateInsuranceRiskPremium',
                    headerName: itemDescr.privateInsuranceRiskPremiumDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.privateInsuranceRiskPremiumDescr}</SortableHeaderComponent>,
                    width: 105,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.privateInsuranceRiskPremiumDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.privateInsuranceRiskPremiumDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                }
            ]
        },

        {
            colId: 'donorShield',
            headerName: 'Donor Shield',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'donorInsuranceAndSupport',
                    field: 'donorInsuranceAndSupport',
                    headerName: itemDescr.donorInsuranceAndSupportDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.donorInsuranceAndSupportDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.donorInsuranceAndSupportDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.donorInsuranceAndSupportDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'donorInsuranceAndSupportLiver',
                    field: 'donorInsuranceAndSupportLiver',
                    headerName: itemDescr.donorInsuranceAndSupportLiverDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.donorInsuranceAndSupportLiverDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.donorInsuranceAndSupportDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.donorInsuranceAndSupportDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'donorShieldFee',
                    field: 'donorShieldFee',
                    headerName: itemDescr.donorShieldDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.donorShieldDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.donorShieldDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.donorShieldDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'donorShieldCredits',
                    field: 'donorShieldCredits',
                    headerName: itemDescr.donorShieldCreditsDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.donorShieldCreditsDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.donorShieldCreditsDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.donorShieldCreditsDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'donorShieldLiverFee',
                    field: 'donorShieldLiverFee',
                    headerName: itemDescr.liverDonorShieldDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.liverDonorShieldDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.liverDonorShieldDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.liverDonorShieldDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'donorShieldCreditsLiver',
                    field: 'donorShieldCreditsLiver',
                    headerName: itemDescr.donorShieldCreditsLiverDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.donorShieldCreditsLiverDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.donorShieldCreditsLiverDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.donorShieldCreditsLiverDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                }
            ]
        },

        {
            colId: 'histo',
            headerName: 'Histo',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'transplantTypingFee',
                    field: 'transplantTypingFee',
                    headerName: itemDescr.transplantTypingFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.transplantTypingFeeDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.transplantTypingFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.transplantTypingFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'typingSwabKit',
                    field: 'typingSwabKit',
                    headerName: itemDescr.kitFulfillmentTypingDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.kitFulfillmentTypingDescr}</SortableHeaderComponent>,
                    width: 85,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.kitFulfillmentTypingDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.kitFulfillmentTypingDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                }
            ]
        },

        {
            colId: 'pwl',
            headerName: 'Pre-Workup Labs',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'pwlQuestFee',
                    field: 'pwlQuestFee',
                    headerName: itemDescr.pwlQuestFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.pwlQuestFeeDescr}</SortableHeaderComponent>,
                    width: 140,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.pwlQuestFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.pwlQuestFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'pwlPerTransplantFee',
                    field: 'pwlPerTransplantFee',
                    headerName: itemDescr.pwlPerTransplantFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.pwlPerTransplantFeeDescr}</SortableHeaderComponent>,
                    width: 140,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.pwlPerTransplantFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.pwlPerTransplantFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                }
            ]
        },

        {
            colId: 'serology',
            headerName: 'Serology',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'serologyFee',
                    field: 'serologyFee',
                    headerName: itemDescr.serologyFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.serologyFeeDescr}</SortableHeaderComponent>,
                    width: 85,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.serologyFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.serologyFeeDescr, formatFee(params.value), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{formatFee(params.value)}</a></div>
                                </OverlayTrigger>
                                : <>{formatFee(params.value)}</>
                        );
                    }
                },

                {
                    colId: 'serologyFeeDirect',
                    field: 'serologyFeeDirect',
                    headerName: itemDescr.serologyFeeDirectDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.serologyFeeDirectDescr}</SortableHeaderComponent>,
                    width: 85,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.serologyFeeDirectDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.serologyFeeDirectDescr, formatFee(params.value), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{formatFee(params.value)}</a></div>
                                </OverlayTrigger>
                                : <>{formatFee(params.value)}</>
                        );
                    }
                },

                {
                    colId: 'serologyKitFee',
                    field: 'serologyKitFee',
                    headerName: itemDescr.kitFulfillmentSerologyDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.kitFulfillmentSerologyDescr}</SortableHeaderComponent>,
                    width: 85,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.kitFulfillmentSerologyDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.kitFulfillmentSerologyDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                }
            ]
        },

        {
            colId: 'ctcb',
            headerName: 'Center to Center Billing',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'streamlinedBillingFee',
                    field: 'streamlinedBillingFee',
                    headerName: itemDescr.ccbHospitalNephrectomyDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.ccbHospitalNephrectomyDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.ccbHospitalNephrectomyDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.ccbHospitalNephrectomyDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'donorWorkupFee',
                    field: 'donorWorkupFee',
                    headerName: itemDescr.ccbDonorWorkupDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.ccbDonorWorkupDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.ccbDonorWorkupDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.ccbDonorWorkupDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
            ]
        },

        {
            colId: 'rmcr',
            headerName: 'RMCR',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'rmcrFeeMinor',
                    field: 'rmcrFeeMinor',
                    headerName: itemDescr.rmcrNonComplianceMinorDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.rmcrNonComplianceMinorDescr}</SortableHeaderComponent>,
                    width: 110,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.rmcrNonComplianceMinorDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.rmcrNonComplianceMinorDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'rmcrFeeMajor',
                    field: 'rmcrFeeMajor',
                    headerName: itemDescr.rmcrNonComplianceMajorDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.rmcrNonComplianceMajorDescr}</SortableHeaderComponent>,
                    width: 110,
                    columnGroupShow: 'open',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.rmcrNonComplianceMajorDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.rmcrNonComplianceMajorDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                }
            ]
        },

        {
            colId: 'membership',
            headerName: 'Annual Membership',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'annualMembershipFee',
                    field: 'annualMembershipFee',
                    headerName: itemDescr.annualMembershipFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.annualMembershipFeeDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.annualMembershipFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.annualMembershipFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },

                {
                    colId: 'annualDcnFee',
                    field: 'annualDcnFee',
                    headerName: itemDescr.dcnAnnualMembershipFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.dcnAnnualMembershipFeeDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.dcnAnnualMembershipFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.dcnAnnualMembershipFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
            ]
        },

        {
            colId: 'misc',
            headerName: 'Misc',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'miscLineItemTotal',
                    field: 'miscLineItemTotal',
                    headerName: itemDescr.customLineItemDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.customLineItemDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.customLineItemDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.customLineItemDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'monthlyInactivityFee',
                    field: 'monthlyInactivityFee',
                    headerName: itemDescr.monthlyInactivityFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.monthlyInactivityFeeDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IInvoiceDetailReport;
                        let popover = hasAdjustments(data.invoiceNumber, itemDescr.monthlyInactivityFeeDescr, lineItems);

                        return (
                            popover == true ?
                                <OverlayTrigger rootClose placement='bottom' trigger={['hover', 'focus']} overlay={<PopoverTemplate>{getPopover(data.invoiceNumber, itemDescr.monthlyInactivityFeeDescr, parseFloat(params.value).toLocaleString(), lineItems)}</PopoverTemplate>}>
                                    <div style={{ backgroundColor: 'yellow' }}><a>{parseFloat(params.value).toLocaleString()}</a></div>
                                </OverlayTrigger>
                                : <>{parseFloat(params.value).toLocaleString()}</>
                        );
                    }
                },
                {
                    colId: 'lostGpsFee',
                    field: 'lostGpsFee',
                    headerName: itemDescr.lostGpsFeeDescr,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.lostGpsFeeDescr}</SortableHeaderComponent>,
                    width: 105,
                    cellRenderer: (props: ICellRendererParams) => parseFloat(props.value).toLocaleString()
                },
            ]
        },
        {
            colId: 'stateTaxes',
            field: 'stateTaxes',
            headerName: itemDescr.stateTaxesDescr,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{itemDescr.stateTaxesDescr}</SortableHeaderComponent>,
            width: 105,
            cellRenderer: (props: ICellRendererParams) => parseFloat(props.value).toLocaleString()
        },

        {
            colId: 'amountDue',
            field: 'amountDue',
            headerName: 'Amount Billed',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Amount<br /> Billed</SortableHeaderComponent>,
            width: 125,
            cellRenderer: (props: ICellRendererParams) => parseFloat(props.value).toLocaleString()
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);
    let showAbbrv = props.headerDescription.showAbbreviation;

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={showAbbrv ? 80 : 130}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No invoices for the selected month &amp; year...</div>}
                pinnedBottomRowData={[props.totals]}
                onGridReady={props.onGridReady}
                getRowStyle={(params: any) => {
                    if (params.node.rowPinned == 'bottom') {
                        return { background: 'lightblue', fontWeight: 600 } as any;
                    }
                }}
            />
        </ReportWrapper>
    );
}

export default Grid;